import {
  CloudRounded,
  FolderRounded,
  StorageRounded,
} from "@mui/icons-material";
import FolderOpenRoundedIcon from "@mui/icons-material/FolderOpenRounded";
import { Box, Typography } from "@mui/material";
import CustomCopyButton from "../../../../../../components/CustomCopyButton";
import SvgToIcon from "../../../../../../icons/SvgToIcon";
import { icons } from "../../../icons/iconExport";
import { DataStoreResourceTypeIconsAndLabels } from "../../../utils";
import TextIntegrationStyle from "../../TextIntegrationStyle";

const AzureFileShareDetailsView = ({ entityData }) => {
  const fileShareData = entityData?.azure_file_share_data;

  if (!fileShareData) {
    return null;
  }

  const [file_share_logo, file_share_label] = Object.keys(
    DataStoreResourceTypeIconsAndLabels
  ).includes(fileShareData.resource_type)
    ? DataStoreResourceTypeIconsAndLabels[fileShareData.resource_type]
    : [null, null];

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {/* Resource Type */}
      {fileShareData.resource_type && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.serverTypeIcon} width="16px" />
            <Typography variant="subtitle2">Resource Type</Typography>
          </Box>
          <Box ml={2} display="flex" alignItems="center" gap={1}>
            {file_share_logo && (
              <SvgToIcon IconLogo={file_share_logo} width="16px" />
            )}
            <TextIntegrationStyle
              title=""
              value={file_share_label || fileShareData.resource_type}
            />
          </Box>
        </Box>
      )}

      {/* Resource ID */}
      {fileShareData.resource_id && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.serialNumberIcon} width="16px" />
            <Typography variant="subtitle2">Resource ID</Typography>
          </Box>
          <Box ml={2} display="flex" alignItems="center" gap={2}>
            <TextIntegrationStyle title="" value={fileShareData.resource_id} />
            <CustomCopyButton
              copyTitle="Resource ID"
              copyText={fileShareData.resource_id}
            />
          </Box>
        </Box>
      )}

      {/* Storage Account */}
      {fileShareData.storage_account_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <StorageRounded sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Storage Account</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={fileShareData.storage_account_name}
            />
          </Box>
        </Box>
      )}

      {/* Share Name */}
      {fileShareData.share_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <FolderRounded sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Share Name</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle title="" value={fileShareData.share_name} />
          </Box>
        </Box>
      )}

      {/* Share Configuration */}
      {(fileShareData.quota_gb || fileShareData.enabled_protocols) && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.configIcon} width="16px" />
            <Typography variant="subtitle2">Share Configuration</Typography>
          </Box>
          <Box ml={2} display="flex" flexDirection="column" gap={1}>
            {fileShareData.quota_gb && (
              <TextIntegrationStyle
                title="Quota:"
                value={`${fileShareData.quota_gb} GB`}
              />
            )}
            {fileShareData.enabled_protocols && (
              <TextIntegrationStyle
                title="Enabled Protocols:"
                value={fileShareData.enabled_protocols}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Performance Settings */}
      {(fileShareData.provisioned_iops ||
        fileShareData.provisioned_bandwidth_mibps) && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.configIcon} width="16px" />
            <Typography variant="subtitle2">Performance Settings</Typography>
          </Box>
          <Box ml={2} display="flex" flexDirection="column" gap={1}>
            {fileShareData.provisioned_iops && (
              <TextIntegrationStyle
                title="Provisioned IOPS:"
                value={fileShareData.provisioned_iops.toLocaleString()}
              />
            )}
            {fileShareData.provisioned_bandwidth_mibps && (
              <TextIntegrationStyle
                title="Provisioned Bandwidth:"
                value={`${fileShareData.provisioned_bandwidth_mibps} MiB/s`}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Usage Information */}
      {(fileShareData.share_usage_bytes || fileShareData.root_squash) && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.databaseIcon} width="16px" />
            <Typography variant="subtitle2">Usage Information</Typography>
          </Box>
          <Box ml={2} display="flex" flexDirection="column" gap={1}>
            {fileShareData.share_usage_bytes && (
              <TextIntegrationStyle
                title="Current Usage:"
                value={`${(
                  fileShareData.share_usage_bytes /
                  (1024 * 1024 * 1024)
                ).toFixed(2)} GB`}
              />
            )}
            {fileShareData.root_squash && (
              <TextIntegrationStyle
                title="Root Squash:"
                value={fileShareData.root_squash}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Resource Group */}
      {fileShareData.resource_group_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <FolderOpenRoundedIcon sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Resource Group</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={fileShareData.resource_group_name}
            />
          </Box>
        </Box>
      )}

      {/* Location */}
      {fileShareData.location && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <SvgToIcon IconLogo={icons.regionServerIcon} width="16px" />
            <Typography variant="subtitle2">Location</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle title="" value={fileShareData.location} />
          </Box>
        </Box>
      )}

      {/* Subscription */}
      {fileShareData.subscription_name && (
        <Box display="flex" flexDirection="column" gap={1}>
          <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
            <CloudRounded sx={{ width: "16px" }} />
            <Typography variant="subtitle2">Subscription</Typography>
          </Box>
          <Box ml={2} display="flex">
            <TextIntegrationStyle
              title=""
              value={fileShareData.subscription_name}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AzureFileShareDetailsView;
